import React from "react";

/**
 * A premium button component with support for variants, sizes, icons, and loading state.
 * Styled with a teal-700 base color palette for a professional, premium look.
 * @date 4/7/2026
 * @version 2.0.0
 * @author Forge
 *
 * @param {Object} props
 * @param {"primary"|"secondary"|"ghost"|"danger"} [props.variant="primary"] - Visual style variant
 * @param {"sm"|"md"|"lg"} [props.size="md"] - Button size
 * @param {React.ReactNode} [props.leftIcon] - Icon element to render before the label
 * @param {React.ReactNode} [props.rightIcon] - Icon element to render after the label
 * @param {boolean} [props.isLoading=false] - Show loading spinner instead of content
 * @param {boolean} [props.disabled=false] - Disable the button
 * @param {"button"|"submit"|"reset"} [props.type="button"] - HTML button type
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {function} [props.onClick] - Click handler
 * @param {React.ReactNode} props.children - Button label content
 * @returns {React.ReactElement}
 */
export default function PremiumButton({
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    isLoading = false,
    disabled = false,
    type = "button",
    className = "",
    onClick,
    children,
}) {
    const isDisabled = isLoading || disabled;

    const baseClasses =
        "inline-flex items-center justify-center font-semibold tracking-wide rounded-lg transition-all duration-200 cursor-pointer select-none";

    const variantClasses = {
        primary:
            "bg-teal-700 text-white hover:bg-teal-600 shadow-md shadow-teal-700/25 hover:shadow-lg hover:shadow-teal-600/30 active:bg-teal-800",
        secondary:
            "bg-white text-teal-700 border-2 border-teal-700/20 hover:bg-teal-50 hover:border-teal-700/40 active:bg-teal-100",
        ghost:
            "bg-transparent text-slate-500 hover:bg-teal-50 hover:text-teal-700 active:bg-teal-100",
        danger:
            "bg-red-600 text-white hover:bg-red-500 shadow-md shadow-red-600/25 hover:shadow-lg active:bg-red-700",
    };

    const sizeClasses = {
        sm: "px-3 py-1.5 text-xs gap-1.5",
        md: "px-4 py-2 text-sm gap-2",
        lg: "px-6 py-3 text-base gap-2.5",
    };

    const disabledClasses = isDisabled
        ? "opacity-40 cursor-not-allowed pointer-events-none"
        : "";

    const allClasses = [
        baseClasses,
        variantClasses[variant] || variantClasses.primary,
        sizeClasses[size] || sizeClasses.md,
        disabledClasses,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button
            type={type}
            className={allClasses}
            onClick={onClick}
            disabled={isDisabled}
        >
            {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
            ) : (
                <>
                    {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
                    {children}
                    {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
                </>
            )}
        </button>
    );
}
